import React, { type ForwardedRef, forwardRef, type ReactNode } from 'react';

import { AnimatePresence } from 'framer-motion';

import { detectIsSafari } from '@pkg/utils';

import { Fallback, Skeleton } from './components';
import { type MediaEventHandler } from './types';

import { ImageAsset, StyledVisualAsset, VideoAsset } from './styles';

// This component does not mash URLs internally.
// Use the `useVisualAssetState()` hook for that!
export interface VisualAssetProps {
    id?: string;
    title?: string;
    videoUrl?: string;
    imageUrl?: string;
    autoPlay?: boolean;
    controls?: boolean;
    // Useful to avoid "flash of broken asset"
    // during `error` or `loading` states.
    hideAsset?: boolean;
    // `children` is used for `error` and `loading` markup.
    children?: ReactNode;
    onError?: MediaEventHandler;
    onLoad?: MediaEventHandler;
}

const IS_SAFARI = detectIsSafari();

function VisualAssetComponent(
    {
        id,
        title,
        videoUrl = '',
        imageUrl = '',
        autoPlay = false,
        controls = false,
        hideAsset = false,
        children,
        onLoad,
        onError,
    }: VisualAssetProps,
    // `ref` is ONLY passed to `<video />` as there can be a
    // need to call `play/pause/stop`. Those concerns are
    // not needed for the `<img />`. Access to each element
    // can also be obtained from the `onLoad()` callback.
    ref: ForwardedRef<HTMLVideoElement>,
) {
    const hasVideo = Boolean(videoUrl.length);
    const posterAsBg = IS_SAFARI && hasVideo && Boolean(imageUrl.length);

    const videoMarkup = hasVideo ? (
        <VideoAsset
            key={`VisualAsset-Video-${id}-${videoUrl}`}
            ref={ref}
            loop
            muted
            playsInline
            disablePictureInPicture
            draggable="false"
            preload="auto"
            id={id}
            src={videoUrl}
            poster={imageUrl}
            title={title}
            autoPlay={autoPlay}
            controls={controls}
            onError={onError}
            onLoadedData={onLoad}
            $hideAsset={hideAsset}
        />
    ) : null;

    const imageMarkup = !hasVideo ? (
        <ImageAsset
            key={`VisualAsset-Image-${id}-${imageUrl}`}
            draggable="false"
            id={id}
            src={imageUrl}
            alt={title}
            onError={onError}
            onLoad={onLoad}
            $hideAsset={hideAsset}
        />
    ) : null;

    return (
        <StyledVisualAsset $bgPoster={posterAsBg ? imageUrl : undefined}>
            {videoMarkup}
            {imageMarkup}

            <AnimatePresence initial={false}>{children}</AnimatePresence>
        </StyledVisualAsset>
    );
}

const VisualAssetForwarded = forwardRef(VisualAssetComponent);
export const VisualAsset = Object.assign(VisualAssetForwarded, { Fallback, Skeleton });
